import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import client from "../lib/api";
import { renderMarkdown } from "../lib/markdown";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Wand2, Edit3, Send, Calendar, EyeOff, Eye,
  CheckCircle2, Clock, FileEdit, Loader2, X, Save, Sparkles, Plus,
  Columns, FileText,
} from "lucide-react";

const fmtDateTime = (s) => {
  if (!s) return "";
  try {
    return new Date(s).toLocaleString("pt-PT", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return s; }
};

const AudienceBadge = ({ audience }) => {
  const a = audience || "public";
  const map = {
    public: { label: "Público", cls: "bg-sky-500/10 text-sky-300" },
    members: { label: "Membros", cls: "bg-indigo-500/10 text-indigo-300" },
    premium: { label: "Só Premium", cls: "bg-[#C5A880]/15 text-[#C5A880]" },
  };
  const m = map[a] || map.public;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${m.cls}`}
          data-testid={`audience-badge-${a}`}>
      {m.label}
    </span>
  );
};

const StateBadge = ({ state, scheduledFor }) => {
  if (state === "published") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 text-[10px] uppercase tracking-wider">
        <CheckCircle2 className="w-3 h-3" /> Publicado
      </span>
    );
  }
  if (state === "scheduled") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#C5A880]/15 text-[#C5A880] text-[10px] uppercase tracking-wider"
            title={`Agendado para ${fmtDateTime(scheduledFor)}`}>
        <Clock className="w-3 h-3" /> Agendado
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-[#A29F9B] text-[10px] uppercase tracking-wider">
      <FileEdit className="w-3 h-3" /> Rascunho
    </span>
  );
};

const ArticleEditorModal = ({ article, onClose, onSaved }) => {
  const [doc, setDoc] = useState(article);
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [extra, setExtra] = useState("");
  const [scheduleAt, setScheduleAt] = useState("");
  const [audience, setAudience] = useState(article.audience || "public");
  // Editor view mode: "edit" (textarea only — default), "split" (textarea +
  // live preview side-by-side on md+), or "preview" (preview only). Stored in
  // localStorage so the admin's preference survives reloads.
  const [previewMode, setPreviewMode] = useState(() => {
    try { return localStorage.getItem("adminArticles.previewMode") || "edit"; }
    catch { return "edit"; }
  });
  const setPreviewModePersist = (m) => {
    setPreviewMode(m);
    try { localStorage.setItem("adminArticles.previewMode", m); } catch { /* ignore */ }
  };

  // Load full body on open
  useEffect(() => {
    (async () => {
      try {
        const r = await client.get(`/admin/articles/${article.slug}`);
        setDoc(r.data);
        setAudience(r.data.audience || "public");
        if (r.data.scheduled_for) {
          // Convert ISO to <input type="datetime-local"> format
          const d = new Date(r.data.scheduled_for);
          const off = d.getTimezoneOffset();
          const local = new Date(d.getTime() - off * 60000);
          setScheduleAt(local.toISOString().slice(0, 16));
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Falha a carregar artigo.");
      }
    })();
  }, [article.slug]);

  const generate = async () => {
    if (doc.body_md && !window.confirm("Já existe corpo. Substituir pela nova geração da IA?")) return;
    setGenBusy(true);
    try {
      // Kick off background job (returns 202 immediately).
      await client.post(`/admin/articles/${doc.slug}/generate`,
        { extra_instructions: extra.trim() || null });
      toast.success("Geração iniciada. A IA pode demorar até 90s…");
      // Poll status every 3s; max 120s to cover slow Claude runs.
      const startedAt = Date.now();
      const timeoutMs = 120_000;
      const pollMs = 3_000;
      // Small helper loop — breaks on completion, error, or timeout.
       
      while (true) {
        await new Promise((r) => setTimeout(r, pollMs));
        try {
          const s = await client.get(`/admin/articles/${doc.slug}/generation-status`);
          if (!s.data?.generating) {
            if (s.data?.error) {
              toast.error(`IA: ${s.data.error}`);
            } else {
              toast.success("Artigo gerado. Revê antes de publicar.");
            }
            break;
          }
        } catch (pollErr) {
          // Transient poll error — keep trying until timeout.
          // eslint-disable-next-line no-console
          console.warn("poll error", pollErr);
        }
        if (Date.now() - startedAt > timeoutMs) {
          toast.error("Demorou demasiado. Recarrega a página para ver o resultado.");
          break;
        }
      }
      const r = await client.get(`/admin/articles/${doc.slug}`);
      setDoc(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "IA falhou.");
    } finally {
      setGenBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await client.put(`/admin/articles/${doc.slug}`, {
        title: doc.title,
        summary: doc.summary,
        category: doc.category,
        body_md: doc.body_md,
        audience,
      });
      toast.success("Guardado.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao guardar.");
    } finally {
      setBusy(false);
    }
  };

  /** Persist current edits before any state-changing action. Returns true
   *  if save succeeded (or wasn't needed); false to abort the caller.
   *  Centralises the "you pasted but didn't click Save" footgun that caused
   *  "O artigo não tem corpo" on publish. */
  const ensureSaved = async () => {
    try {
      await client.put(`/admin/articles/${doc.slug}`, {
        title: doc.title,
        summary: doc.summary,
        category: doc.category,
        body_md: doc.body_md,
        audience,
      });
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível guardar antes da acção.");
      return false;
    }
  };

  const publishNow = async () => {
    // Client-side guard: avoid an unnecessary roundtrip when there's clearly
    // no content. Matches the backend's min-200-chars rule (loosely — backend
    // still enforces strictly after save).
    if (!doc.body_md || doc.body_md.trim().length < 200) {
      toast.error("Adiciona o corpo do artigo antes de publicar (mínimo ~200 caracteres).");
      return;
    }
    if (!window.confirm(
      "Publicar AGORA? O artigo fica imediatamente visível em /articles e " +
      "o email de notificação será enviado a todos os utilizadores opt-in."
    )) return;
    setBusy(true);
    try {
      if (!(await ensureSaved())) return;
      await client.post(`/admin/articles/${doc.slug}/publish`, {
        publish_now: true, audience,
      });
      toast.success("Artigo publicado. Emails a sair em background.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao publicar.");
    } finally {
      setBusy(false);
    }
  };

  const schedule = async () => {
    if (!scheduleAt) { toast.error("Escolhe data e hora."); return; }
    if (!doc.body_md || doc.body_md.trim().length < 200) {
      toast.error("Adiciona o corpo do artigo antes de agendar (mínimo ~200 caracteres).");
      return;
    }
    const isoLocal = new Date(scheduleAt).toISOString();
    if (!window.confirm(`Agendar publicação para ${fmtDateTime(isoLocal)}?`)) return;
    setBusy(true);
    try {
      if (!(await ensureSaved())) return;
      await client.post(`/admin/articles/${doc.slug}/publish`, {
        scheduled_for: isoLocal, audience,
      });
      toast.success("Agendado.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao agendar.");
    } finally {
      setBusy(false);
    }
  };

  const unpublish = async () => {
    if (!window.confirm("Despublicar? Volta para rascunho e desaparece da listagem pública.")) return;
    setBusy(true);
    try {
      await client.post(`/admin/articles/${doc.slug}/unpublish`);
      toast.success("Despublicado.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha.");
    } finally {
      setBusy(false);
    }
  };

  const wordCount = (doc.body_md || "").split(/\s+/).filter(Boolean).length;

  // Sanitized HTML preview of the current draft. Memoized so we only re-parse
  // markdown when the body actually changes — not on every audience/title
  // re-render. DOMPurify keeps us safe from injected <script> in pasted
  // content (a real concern when admins paste from Word/Notion).
  const previewHtml = useMemo(
    () => DOMPurify.sanitize(renderMarkdown(doc.body_md || ""), {
      ADD_ATTR: ["target", "rel"],
    }),
    [doc.body_md],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
         onClick={onClose} data-testid="article-editor-backdrop">
      <div className="bg-[#161920] border border-white/10 rounded-2xl p-6 md:p-8 max-w-4xl w-full max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}
           data-testid="article-editor-modal">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="min-w-0">
            <span className="label-uppercase">Artigo</span>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <h2 className="font-display text-2xl text-[#F5F2EE] truncate">{doc.title}</h2>
              <AudienceBadge audience={audience} />
              <StateBadge state={doc.state} scheduledFor={doc.scheduled_for} />
            </div>
            <p className="text-xs text-[#A29F9B] mt-1 font-mono">{doc.slug}</p>
          </div>
          <button onClick={onClose} className="text-[#A29F9B] hover:text-[#F5F2EE]"
                  data-testid="article-editor-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Meta fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <label className="text-xs text-[#A29F9B] uppercase tracking-widest col-span-2">
            Título
            <input value={doc.title || ""}
                   onChange={(e) => setDoc({ ...doc, title: e.target.value })}
                   className="mt-1 w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm normal-case tracking-normal text-[#F5F2EE]"
                   data-testid="article-title" />
          </label>
          <label className="text-xs text-[#A29F9B] uppercase tracking-widest">
            Categoria
            <input value={doc.category || ""}
                   onChange={(e) => setDoc({ ...doc, category: e.target.value })}
                   className="mt-1 w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm normal-case tracking-normal text-[#F5F2EE]"
                   data-testid="article-category" />
          </label>
          <label className="text-xs text-[#A29F9B] uppercase tracking-widest">
            Resumo
            <input value={doc.summary || ""}
                   onChange={(e) => setDoc({ ...doc, summary: e.target.value })}
                   className="mt-1 w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm normal-case tracking-normal text-[#F5F2EE]"
                   data-testid="article-summary" />
          </label>
        </div>

        {/* Audience selector — who can read this article */}
        <div className="bg-[#0C0E12] border border-white/10 rounded-xl p-4 mb-5">
          <p className="text-xs text-[#A29F9B] uppercase tracking-widest mb-3">Audiência</p>
          <div className="flex flex-wrap gap-2" data-testid="article-audience">
            {[
              { v: "public", label: "Público", hint: "Qualquer pessoa (anónimo + registado + premium)" },
              { v: "members", label: "Membros", hint: "Requer login — anónimo vê sumário + CTA" },
              { v: "premium", label: "Só Premium", hint: "Só subscritores Premium leem o corpo" },
            ].map((o) => (
              <button key={o.v}
                      type="button"
                      onClick={() => setAudience(o.v)}
                      title={o.hint}
                      data-testid={`audience-${o.v}`}
                      className={`px-3 py-2 rounded-lg text-xs uppercase tracking-widest border transition-colors ${
                        audience === o.v
                          ? "bg-[#C5A880]/20 border-[#C5A880] text-[#F5F2EE]"
                          : "bg-transparent border-white/10 text-[#A29F9B] hover:border-white/30"
                      }`}>
                {o.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-[#A29F9B] mt-2">
            {audience === "public" && "Qualquer pessoa lê o conteúdo completo (ideal para SEO/lead magnet)."}
            {audience === "members" && "Só utilizadores com conta (free ou premium) leem o corpo. Anónimos veem só o sumário com CTA 'Entrar'."}
            {audience === "premium" && "Só subscritores Premium leem o corpo. Restantes veem sumário com CTA 'Subscrever Premium'."}
          </p>
        </div>

        {/* AI generate */}
        <div className="bg-[#0C0E12] border border-[#C5A880]/30 rounded-xl p-4 mb-5">
          <p className="text-xs text-[#C5A880] uppercase tracking-widest mb-2 inline-flex items-center gap-1.5">
            <Wand2 className="w-3 h-3" /> Gerar com IA
          </p>
          <textarea value={extra} onChange={(e) => setExtra(e.target.value)}
                    placeholder="Instruções adicionais para a IA (opcional). Ex: 'Foca casos de relações longas (>5 anos)'"
                    rows={2} maxLength={2000}
                    className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F5F2EE] mb-2"
                    data-testid="article-extra-instructions" />
          <button onClick={generate} disabled={genBusy}
                  className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="article-generate">
            {genBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            {genBusy ? "A gerar… (até 2 min)" : (doc.body_md ? "Re-gerar com IA" : "Gerar com IA")}
          </button>
        </div>

        {/* Body editor — with view-mode toggle (edit / split / preview).
            Split goes side-by-side on md+ and stacks on small screens. */}
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <label className="text-xs text-[#A29F9B] uppercase tracking-widest">
            Conteúdo (Markdown) — {wordCount} palavras
          </label>
          <div className="inline-flex rounded-lg border border-white/10 overflow-hidden text-xs"
               data-testid="article-view-toggle">
            {[
              { v: "edit", label: "Editar", Icon: FileEdit },
              { v: "split", label: "Dividido", Icon: Columns },
              { v: "preview", label: "Pré-visualizar", Icon: FileText },
            ].map(({ v, label, Icon }) => (
              <button key={v}
                      type="button"
                      onClick={() => setPreviewModePersist(v)}
                      data-testid={`article-view-${v}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 transition-colors ${
                        previewMode === v
                          ? "bg-[#C5A880]/20 text-[#F5F2EE]"
                          : "bg-transparent text-[#A29F9B] hover:text-[#F5F2EE]"
                      }`}>
                <Icon className="w-3 h-3" /> {label}
              </button>
            ))}
          </div>
        </div>
        <div className={`grid gap-3 ${previewMode === "split" ? "md:grid-cols-2" : "grid-cols-1"}`}>
          {previewMode !== "preview" && (
            <textarea value={doc.body_md || ""}
                      onChange={(e) => setDoc({ ...doc, body_md: e.target.value })}
                      rows={18} placeholder="O corpo do artigo em Markdown…"
                      className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-3 text-sm text-[#F5F2EE] font-mono leading-relaxed"
                      data-testid="article-body" />
          )}
          {previewMode !== "edit" && (
            <div className="bg-[#0C0E12] border border-white/10 rounded-lg px-4 py-3 max-h-[480px] overflow-y-auto"
                 data-testid="article-body-preview">
              {doc.body_md ? (
                <div className="prose prose-invert max-w-none"
                     dangerouslySetInnerHTML={{ __html: previewHtml }} />
              ) : (
                <p className="text-xs text-[#A29F9B] italic">
                  Pré-visualização vazia — escreve ou cola Markdown na coluna ao lado.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Schedule controls */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6 pt-5 border-t border-white/5">
          <button onClick={save} disabled={busy}
                  className="btn-secondary inline-flex items-center justify-center gap-1.5"
                  data-testid="article-save">
            <Save className="w-4 h-4" /> Guardar
          </button>
          <div className="flex items-center gap-2">
            <input type="datetime-local" value={scheduleAt}
                   onChange={(e) => setScheduleAt(e.target.value)}
                   className="flex-1 bg-[#0C0E12] border border-white/10 rounded-lg px-2 py-2 text-xs text-[#F5F2EE]"
                   data-testid="article-schedule-at" />
            <button onClick={schedule} disabled={busy || !scheduleAt}
                    className="text-sm inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-[#C5A880]/40 bg-[#C5A880]/10 text-[#C5A880] hover:bg-[#C5A880]/20 disabled:opacity-50 whitespace-nowrap"
                    data-testid="article-schedule">
              <Calendar className="w-3.5 h-3.5" /> Agendar
            </button>
          </div>
          {doc.state === "published" ? (
            <button onClick={unpublish} disabled={busy}
                    className="text-sm inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 disabled:opacity-50"
                    data-testid="article-unpublish">
              <EyeOff className="w-4 h-4" /> Despublicar
            </button>
          ) : (
            <button onClick={publishNow} disabled={busy}
                    className="btn-primary inline-flex items-center justify-center gap-1.5"
                    data-testid="article-publish-now">
              <Send className="w-4 h-4" /> Publicar agora
            </button>
          )}
        </div>

        {doc.state === "published" && (
          <p className="text-xs text-[#A29F9B] mt-3 text-center">
            Notificados {doc.notified_count || 0} utilizadores · Publicado {fmtDateTime(doc.published_at)}
          </p>
        )}
      </div>
    </div>
  );
};

const ThemeBatchPanel = ({ onCreated }) => {
  const [theme, setTheme] = useState("");
  const [count, setCount] = useState(5);
  const [loading, setLoading] = useState(false);
  const [ideas, setIdeas] = useState([]); // {slug,title,summary,category,selected}
  const [creating, setCreating] = useState(false);

  const generateIdeas = async () => {
    if (theme.trim().length < 3) { toast.error("O tema é demasiado curto."); return; }
    setLoading(true);
    try {
      const r = await client.post("/admin/articles/generate-theme",
        { theme: theme.trim(), count });
      setIdeas((r.data?.ideas || []).map((it) => ({ ...it, selected: true })));
      toast.success(`${r.data?.ideas?.length || 0} ideias geradas.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao gerar ideias.");
    } finally {
      setLoading(false);
    }
  };

  const updateIdea = (i, patch) => {
    setIdeas((prev) => prev.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  };

  const createDrafts = async () => {
    const picked = ideas.filter((it) => it.selected)
      .map(({ slug, title, summary, category }) => ({ slug, title, summary, category }));
    if (!picked.length) { toast.error("Selecciona pelo menos uma ideia."); return; }
    setCreating(true);
    try {
      const r = await client.post("/admin/articles/create-batch", { items: picked });
      const nc = r.data?.created?.length || 0;
      const ns = r.data?.skipped?.length || 0;
      toast.success(`${nc} draft(s) criado(s)${ns ? ` · ${ns} já existiam` : ""}.`);
      setIdeas([]);
      setTheme("");
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar drafts.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <section className="bg-[#161920] border border-[#C5A880]/30 rounded-2xl p-5 md:p-6"
             data-testid="theme-batch-panel">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-[#C5A880]" />
        <h2 className="font-display text-xl text-[#F5F2EE]">Gerar em lote por tema</h2>
      </div>
      <p className="text-xs text-[#A29F9B] mb-4">
        Dá um tema (ex: "Ciúmes em relações modernas"). A IA propõe várias
        ideias de artigos com ângulos complementares. Tu escolhes/editas e são
        criados como drafts — o corpo é gerado depois, um a um.
      </p>

      <div className="flex gap-2 flex-wrap">
        <input value={theme} onChange={(e) => setTheme(e.target.value)}
               placeholder="Tema…"
               className="flex-1 min-w-[240px] bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F5F2EE]"
               data-testid="theme-input" />
        <select value={count} onChange={(e) => setCount(parseInt(e.target.value, 10))}
                className="bg-[#0C0E12] border border-white/10 rounded-lg px-2 py-2 text-sm text-[#F5F2EE]"
                data-testid="theme-count">
          {[3, 5, 7, 10].map((n) => <option key={n} value={n}>{n} ideias</option>)}
        </select>
        <button onClick={generateIdeas} disabled={loading || !theme.trim()}
                className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="theme-generate">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
          {loading ? "A gerar…" : "Propor ideias"}
        </button>
      </div>

      {ideas.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="theme-ideas-list">
          {ideas.map((it, i) => (
            <div key={it.slug + i}
                 className={`rounded-xl p-4 border transition-colors ${
                   it.selected ? "bg-[#0C0E12] border-[#C5A880]/40" : "bg-[#0C0E12]/60 border-white/5 opacity-60"
                 }`}
                 data-testid={`theme-idea-${i}`}>
              <div className="flex items-start gap-3">
                <input type="checkbox" checked={!!it.selected}
                       onChange={(e) => updateIdea(i, { selected: e.target.checked })}
                       className="mt-1.5 accent-[#C5A880]"
                       data-testid={`theme-idea-check-${i}`} />
                <div className="flex-1 space-y-2">
                  <input value={it.title}
                         onChange={(e) => updateIdea(i, { title: e.target.value })}
                         className="w-full bg-transparent border-b border-white/10 focus:border-[#C5A880] text-[#F5F2EE] font-display text-lg px-0 py-1 outline-none"
                         data-testid={`theme-idea-title-${i}`} />
                  <div className="flex gap-2 items-center flex-wrap text-xs">
                    <span className="text-[#A29F9B]">Categoria:</span>
                    <input value={it.category}
                           onChange={(e) => updateIdea(i, { category: e.target.value })}
                           className="bg-[#161920] border border-white/10 rounded px-2 py-1 text-[#F5F2EE] text-xs"
                           data-testid={`theme-idea-cat-${i}`} />
                    <span className="text-[#A29F9B] font-mono">· {it.slug}</span>
                  </div>
                  <textarea value={it.summary}
                            onChange={(e) => updateIdea(i, { summary: e.target.value })}
                            rows={2}
                            className="w-full bg-[#161920] border border-white/10 rounded-lg px-3 py-2 text-xs text-[#DCD7D2]"
                            data-testid={`theme-idea-summary-${i}`} />
                </div>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
            <span className="text-xs text-[#A29F9B]">
              {ideas.filter((x) => x.selected).length} de {ideas.length} seleccionadas
            </span>
            <button onClick={createDrafts} disabled={creating}
                    className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="theme-create-drafts">
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {creating ? "A criar…" : "Criar drafts"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

const AdminArticles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  // Multi-select for batch body generation. Holds slugs that the admin has
  // ticked. We poll generation status only for these so the rest of the UI
  // stays cheap.
  const [selected, setSelected] = useState(new Set());
  const [batchBusy, setBatchBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await client.get("/admin/articles");
      setArticles(r.data?.articles || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao listar.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-poll while any article in the visible list is currently generating
  // (e.g. after a batch generate). Refreshes the list every 4s so the admin
  // sees char_count/state update without a manual click.
  useEffect(() => {
    const anyBusy = articles.some((a) => a.generating);
    if (!anyBusy) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [articles, load]);

  const toggleSelect = (slug, e) => {
    e?.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  const generateBatchBodies = async () => {
    const slugs = Array.from(selected);
    if (!slugs.length) { toast.error("Selecciona pelo menos um artigo."); return; }
    if (!window.confirm(
      `Gerar corpo IA para ${slugs.length} artigo(s) em paralelo? ` +
      "Pode demorar 1-2 minutos e fica em draft."
    )) return;
    setBatchBusy(true);
    try {
      const r = await client.post("/admin/articles/generate-batch", { slugs });
      const queued = r.data?.queued?.length || 0;
      const skipped = r.data?.skipped?.length || 0;
      toast.success(`${queued} em geração${skipped ? ` · ${skipped} ignorados` : ""}.`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao iniciar batch.");
    } finally {
      setBatchBusy(false);
    }
  };

  const runScheduled = async () => {
    try {
      const r = await client.post("/admin/articles/run-scheduled");
      const n = r.data?.count || 0;
      toast.success(n > 0 ? `${n} artigo(s) publicados.` : "Nada agendado para agora.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0C0E12] text-[#F5F2EE]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <Link to="/admin"
              className="inline-flex items-center gap-2 text-sm text-[#A29F9B] hover:text-[#C5A880]"
              data-testid="back-to-admin">
          <ArrowLeft className="w-4 h-4" /> Voltar ao Admin
        </Link>

        <header className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="label-uppercase">Editorial</span>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">Artigos</h1>
            <p className="text-[#A29F9B] text-sm mt-2 max-w-xl">
              Gere com IA, edita o Markdown e agenda a publicação. No momento certo,
              o artigo é enviado por email a todos os utilizadores opt-in.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading}
                    className="text-sm inline-flex items-center gap-1.5 text-[#A29F9B] hover:text-[#C5A880]"
                    data-testid="articles-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Atualizar
            </button>
            <button onClick={runScheduled}
                    className="btn-secondary text-sm inline-flex items-center gap-1.5"
                    data-testid="articles-run-scheduled">
              <Clock className="w-3.5 h-3.5" />
              Correr agendados
            </button>
          </div>
        </header>

        <ThemeBatchPanel onCreated={load} />

        {/* Batch generate bar — only shown when there's a selection */}
        {selected.size > 0 && (
          <div className="bg-[#161920] border border-[#C5A880]/40 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap"
               data-testid="batch-actions-bar">
            <span className="text-sm text-[#F5F2EE]">
              {selected.size} artigo(s) seleccionado(s)
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => setSelected(new Set())}
                      className="text-xs text-[#A29F9B] hover:text-[#F5F2EE]"
                      data-testid="batch-clear">
                Limpar selecção
              </button>
              <button onClick={generateBatchBodies} disabled={batchBusy}
                      className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="batch-generate-bodies">
                {batchBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {batchBusy ? "A iniciar…" : `Gerar corpo IA (${selected.size})`}
              </button>
            </div>
          </div>
        )}

        <div className="space-y-3" data-testid="article-list">
          {loading ? (
            <p className="text-[#A29F9B] text-sm">A carregar…</p>
          ) : articles.length === 0 ? (
            <p className="text-[#A29F9B] text-sm italic">Sem artigos.</p>
          ) : (
            articles.map((a) => (
              <div key={a.slug}
                   className="w-full bg-[#161920] hover:bg-[#1B1F27] border border-white/5 hover:border-[#C5A880]/40 rounded-2xl p-5 transition-colors flex items-start gap-3"
                   data-testid={`article-row-${a.slug}`}>
                <input type="checkbox" checked={selected.has(a.slug)}
                       onChange={(e) => toggleSelect(a.slug, e)}
                       onClick={(e) => e.stopPropagation()}
                       className="mt-1.5 accent-[#C5A880] flex-shrink-0"
                       data-testid={`article-select-${a.slug}`} />
                <button onClick={() => setEditing(a)}
                        className="flex-1 text-left min-w-0"
                        data-testid={`article-open-${a.slug}`}>
                  <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                    <h3 className="font-display text-xl text-[#F5F2EE]">{a.title}</h3>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {a.generating && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 text-[10px] uppercase tracking-wider"
                              data-testid={`article-generating-${a.slug}`}>
                          <Loader2 className="w-3 h-3 animate-spin" /> A gerar
                        </span>
                      )}
                      <AudienceBadge audience={a.audience} />
                      <StateBadge state={a.state} scheduledFor={a.scheduled_for} />
                    </div>
                  </div>
                  <p className="text-sm text-[#A29F9B] mb-2">{a.summary}</p>
                  <div className="flex items-center gap-3 text-xs text-[#A29F9B]/80 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full bg-white/5">{a.category}</span>
                    {a.published_at && (
                      <span className="inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                        {fmtDateTime(a.published_at)}
                      </span>
                    )}
                    {a.scheduled_for && (
                      <span className="inline-flex items-center gap-1 text-[#C5A880]">
                        <Clock className="w-3 h-3" />
                        {fmtDateTime(a.scheduled_for)}
                      </span>
                    )}
                    {a.notified_count > 0 && (
                      <span className="text-[#A29F9B]">
                        {a.notified_count} notificados
                      </span>
                    )}
                    {a.generate_error && (
                      <span className="text-rose-400 truncate max-w-[260px]" title={a.generate_error}>
                        Erro IA: {a.generate_error}
                      </span>
                    )}
                    <span className="ml-auto inline-flex items-center gap-1 text-[#C5A880]">
                      <Edit3 className="w-3 h-3" /> Abrir
                    </span>
                  </div>
                </button>
              </div>
            ))
          )}
        </div>
      </main>
      <Footer />

      {editing && (
        <ArticleEditorModal article={editing}
                            onClose={() => setEditing(null)}
                            onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
};

export default AdminArticles;
